import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/use-currency";
import { Loader2, Download, TrendingUp, DollarSign, Users, Calendar } from "lucide-react";
import { format } from "date-fns";

interface Salesperson {
    id: string;
    name: string;
    email: string;
    total_commission_earned: number;
    total_commission_paid: number;
}

interface Redemption {
    id: string;
    referral_code_id: string;
    booking_id: string;
    customer_email: string;
    original_amount: number;
    discount_amount: number;
    final_amount: number;
    commission_amount: number;
    redeemed_at: string;
    referral_codes?: {
        code: string;
        salesperson_id: string;
    };
    bookings?: {
        customer_name: string;
    };
}

export function CommissionReports() {
    const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
    const [redemptions, setRedemptions] = useState<Redemption[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSalesperson, setSelectedSalesperson] = useState<string>("all");
    const [selectedStatus, setSelectedStatus] = useState<string>("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const { toast } = useToast();
    const { formatPrice } = useCurrency();

    useEffect(() => {
        fetchData();
    }, [selectedSalesperson, selectedStatus, startDate, endDate]);

    const fetchData = async () => {
        setLoading(true);

        // Fetch salespeople
        const { data: peopleData } = await supabase
            .from('salespeople')
            .select('*')
            .order('name');

        setSalespeople(peopleData || []);

        // Fetch redemptions with filters
        let query = supabase
            .from('referral_code_redemptions')
            .select(`
        *,
        referral_codes(code, salesperson_id),
        bookings(customer_name, booking_status)
      `)
            .order('redeemed_at', { ascending: false });

        if (selectedSalesperson !== "all") {
            query = query.eq('salesperson_id', selectedSalesperson);
        }

        if (startDate) {
            query = query.gte('redeemed_at', new Date(startDate).toISOString());
        }

        if (endDate) {
            const endDateTime = new Date(endDate);
            endDateTime.setHours(23, 59, 59, 999);
            query = query.lte('redeemed_at', endDateTime.toISOString());
        }

        const { data: redemptionsData, error } = await query;

        if (error) {
            toast({
                title: "Error fetching data",
                description: error.message,
                variant: "destructive"
            });
        } else {
            setRedemptions(redemptionsData || []);
        }

        setLoading(false);
    };

    const exportToCSV = () => {
        const headers = ['Date', 'Salesperson', 'Code', 'Customer', 'Original Amount', 'Discount', 'Final Amount', 'Commission'];

        const rows = redemptions.map(r => {
            const salesperson = salespeople.find(s => s.id === r.referral_codes?.salesperson_id);
            return [
                format(new Date(r.redeemed_at), 'yyyy-MM-dd HH:mm'),
                salesperson?.name || 'Unknown',
                r.referral_codes?.code || '',
                r.bookings?.customer_name || r.customer_email,
                r.original_amount,
                r.discount_amount,
                r.final_amount,
                r.commission_amount
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `commission-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        toast({ title: "Report Exported", description: "CSV file downloaded successfully" });
    };

    const completedRedemptions = redemptions.filter(r => r.bookings?.booking_status === 'completed');
    const totalCommissionEarned = completedRedemptions.reduce((sum, r) => sum + r.commission_amount, 0);
    const totalPotentialCommission = redemptions
        .filter(r => r.bookings?.booking_status !== 'completed' && r.bookings?.booking_status !== 'cancelled')
        .reduce((sum, r) => sum + r.commission_amount, 0);
    const totalDiscount = redemptions.reduce((sum, r) => sum + r.discount_amount, 0);
    const totalRevenue = redemptions.reduce((sum, r) => sum + r.final_amount, 0);

    const filteredSalespeople = selectedSalesperson === "all"
        ? salespeople
        : salespeople.filter(s => s.id === selectedSalesperson);

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-3xl font-bold tracking-tight">Commission Reports</h2>
                <p className="text-muted-foreground">View and analyze referral commission data.</p>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
                        <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{redemptions.length}</div>
                        <p className="text-xs text-muted-foreground">
                            {selectedSalesperson === "all" ? "All salespeople" : "Filtered"}
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Earned Commission</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-600">{formatPrice(totalCommissionEarned)}</div>
                        <p className="text-xs text-muted-foreground">
                            From completed sessions
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Potential Commission</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-blue-600">{formatPrice(totalPotentialCommission)}</div>
                        <p className="text-xs text-muted-foreground">
                            From pending/confirmed
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Discounts</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-orange-600">{formatPrice(totalDiscount)}</div>
                        <p className="text-xs text-muted-foreground">
                            Given to customers
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Revenue Generated</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatPrice(totalRevenue)}</div>
                        <p className="text-xs text-muted-foreground">
                            After discounts
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card>
                <CardHeader>
                    <CardTitle>Filters</CardTitle>
                    <CardDescription>Filter commission data by salesperson and date range</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 md:grid-cols-4">
                        <div className="space-y-2">
                            <Label>Salesperson</Label>
                            <Select value={selectedSalesperson} onValueChange={setSelectedSalesperson}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Salespeople</SelectItem>
                                    {salespeople.map(person => (
                                        <SelectItem key={person.id} value={person.id}>
                                            {person.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Booking Status</Label>
                            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="completed">Completed (Earned)</SelectItem>
                                    <SelectItem value="confirmed">Confirmed</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Start Date</Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>End Date</Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>&nbsp;</Label>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setSelectedSalesperson("all");
                                        setSelectedStatus("all");
                                        setStartDate("");
                                        setEndDate("");
                                    }}
                                    className="flex-1"
                                >
                                    Clear
                                </Button>
                                <Button onClick={exportToCSV} className="flex-1">
                                    <Download className="h-4 w-4 mr-2" />
                                    Export
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Salesperson Summary */}
            {filteredSalespeople.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Salesperson Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead className="text-right">Total Earned</TableHead>
                                    <TableHead className="text-right">Total Paid</TableHead>
                                    <TableHead className="text-right">Pending</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredSalespeople.map(person => {
                                    const pending = person.total_commission_earned - person.total_commission_paid;
                                    return (
                                        <TableRow key={person.id}>
                                            <TableCell className="font-medium">{person.name}</TableCell>
                                            <TableCell className="text-muted-foreground">{person.email}</TableCell>
                                            <TableCell className="text-right text-green-600 font-semibold">
                                                {formatPrice(person.total_commission_earned)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatPrice(person.total_commission_paid)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Badge variant={pending > 0 ? "default" : "secondary"}>
                                                    {formatPrice(pending)}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Redemptions Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Redemption Details</CardTitle>
                    <CardDescription>Individual referral code redemptions</CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Loader2 className="animate-spin h-8 w-8 text-primary" />
                        </div>
                    ) : redemptions.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No redemptions found for the selected filters.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Salesperson</TableHead>
                                        <TableHead>Code</TableHead>
                                        <TableHead>Customer</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Original</TableHead>
                                        <TableHead className="text-right">Discount</TableHead>
                                        <TableHead className="text-right">Final</TableHead>
                                        <TableHead className="text-right">Commission</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {redemptions.map(redemption => {
                                        const salesperson = salespeople.find(s => s.id === redemption.referral_codes?.salesperson_id);
                                        return (
                                            <TableRow key={redemption.id}>
                                                <TableCell className="text-sm">
                                                    {format(new Date(redemption.redeemed_at), 'MMM dd, yyyy HH:mm')}
                                                </TableCell>
                                                <TableCell className="font-medium">
                                                    {salesperson?.name || 'Unknown'}
                                                </TableCell>
                                                <TableCell>
                                                    <code className="text-xs bg-muted px-2 py-1 rounded">
                                                        {redemption.referral_codes?.code}
                                                    </code>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm">
                                                        <div className="font-medium">{redemption.bookings?.customer_name}</div>
                                                        <div className="text-muted-foreground text-xs">{redemption.customer_email}</div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={
                                                        redemption.bookings?.booking_status === 'completed' ? 'default' :
                                                            redemption.bookings?.booking_status === 'cancelled' ? 'destructive' : 'secondary'
                                                    } className="capitalize py-0 h-5 text-[10px]">
                                                        {redemption.bookings?.booking_status}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right text-muted-foreground">
                                                    {formatPrice(redemption.original_amount)}
                                                </TableCell>
                                                <TableCell className="text-right text-orange-600">
                                                    -{formatPrice(redemption.discount_amount)}
                                                </TableCell>
                                                <TableCell className="text-right font-semibold">
                                                    {formatPrice(redemption.final_amount)}
                                                </TableCell>
                                                <TableCell className="text-right text-green-600 font-semibold">
                                                    {formatPrice(redemption.commission_amount)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
